import { PASSWORD_MIN_LENGTH } from '@mnd-apps/public/constants';
import { isBlockedEmailDomain } from '@mnd-apps/public/lib/emailDomainBlockList';
import { type FieldNames, FORM_FIELD } from '@mnd-frontend/ui/public/components/Form/index.type';
import isEmail from 'nice-is-email';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useFormValidation = () => {
  const { t } = useTranslation();
  const [fieldErrors, setFieldErrors] = useState<{ [fieldName in FieldNames]: string } | {}>({});
  const [validFields, setValidFields] = useState<{ [fieldName in FieldNames]: boolean } | {}>({});

  const setFieldError = (fieldName: FieldNames, error?: string | undefined) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      if (!!error && typeof error === 'string') {
        next[fieldName] = error;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const setValidField = (fieldName: FieldNames, valid: boolean) => {
    setValidFields(prev => {
      const next = { ...prev };
      if (valid) {
        next[fieldName] = valid;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const validateRequired = (fieldName: FieldNames, value: string) => {
    const isFilled = value.trim().length > 0;

    if (!isFilled) {
      setFieldError(fieldName, t('form.validation.required'));
      setValidField(fieldName, false);
      return;
    }

    setFieldError(fieldName);
    setValidField(fieldName, true);
  };

  const validatePassword = ({
    value,
    errorMessage,
    isRequired = true,
  }: {
    value: string;
    errorMessage?: string;
    isRequired?: boolean;
  }) => {
    const hasLowerCase = value !== value.toUpperCase();
    const hasUpperCase = value !== value.toLowerCase();
    const hasNumber = /\d/.test(value);

    if (
      (isRequired && value.length < PASSWORD_MIN_LENGTH) ||
      value.length === 0 ||
      !hasLowerCase ||
      !hasUpperCase ||
      !hasNumber
    ) {
      setFieldError(FORM_FIELD.PASSWORD, errorMessage ?? t('form.validation.password.format'));
      setValidField(FORM_FIELD.PASSWORD, false);
      return;
    }
    setFieldError(FORM_FIELD.PASSWORD);
    setValidField(FORM_FIELD.PASSWORD, true);
  };

  const validateEmail = ({
    value,
    errorMessage,
    isRequired = true,
  }: {
    value: string;
    errorMessage?: string;
    isRequired?: boolean;
  }) => {
    if (isRequired) {
      if (!isEmail(value)) {
        setFieldError(FORM_FIELD.EMAIL, errorMessage ?? t('form.validation.email.format'));
        setValidField(FORM_FIELD.EMAIL, false);
        return;
      } else if (isBlockedEmailDomain(value)) {
        setFieldError(
          FORM_FIELD.EMAIL,
          t('form.validation.email.blocked', { domain: value.split('@')[1] }),
        );
        setValidField(FORM_FIELD.EMAIL, false);
        return;
      }
    }

    setFieldError(FORM_FIELD.EMAIL);
    setValidField(FORM_FIELD.EMAIL, true);
  };

  const validatePresence = (
    fieldName: FieldNames,
    data: {
      value: string;
      isRequired?: boolean;
      translationKey?: string;
    },
  ) => {
    if (data.isRequired) {
      return validateRequired(fieldName, data.value);
    }

    setFieldError(fieldName);
    setValidField(fieldName, true);
  };

  const validateCheckbox = (
    fieldName: FieldNames,
    data: {
      value: string | undefined;
      isRequired: boolean;
      errorMessage?: string;
    },
  ) => {
    if ((data.isRequired && data.value !== 'on') || data.value !== 'true') {
      setFieldError(fieldName, data.errorMessage ?? t('form.validation.checkbox.required'));
      setValidField(fieldName, false);
      return;
    }

    setFieldError(fieldName);
    setValidField(fieldName, true);
  };

  const clearValidation = () => {
    setFieldErrors({});
    setValidFields({});
  };

  return {
    validateCheckbox,
    validateEmail,
    validatePassword,
    validatePresence,
    clearValidation,
    fieldErrors,
    validFields,
    setFieldError,
    setValidField,
  };
};
